<template>
  <div class="graph-module">
    <div class="graph-header">
      <div
        v-if="dashboardModule.name"
        class="module-name">
        {{ dashboardModule.name }}
      </div>

      <div class="actions">
        <SnapQLDebugButton
          :dashboard-module="dashboardModule"
          :dashboard-data-context="dashboardDataContext"
          :dashboard-key="dashboardKey" />
        <ExportButton
          :dashboard-module="dashboardModule"
          :dashboard-data-context="dashboardDataContext"
          :dashboard-key="dashboardKey" />
        <ShareButton
          :dashboard-module="dashboardModule"
          :dashboard-key="dashboardKey"
          :shared-module-id="sharedModuleId"
          @shared="$emit('module-shared', { moduleKey: dashboardModule.key, sharedModuleId: $event })"
          @unshared="$emit('module-unshared', { moduleKey: dashboardModule.key })" />
      </div>
    </div>
    <div
      v-if="queryError"
      class="query-error">
      <b-icon
        icon="alert-circle-outline"
        size="is-small" />
      <span>{{ queryError }}</span>
    </div>
    <div class="graph-body">
      <div class="graph-container">
        <template v-if="isLoading">
          <b-skeleton
            height="568px"
            width="100%"
            :animated="true" />
        </template>
        <template v-else>
          <HeatmapGraph
            :measure-label="measureLabel"
            :measure-data-type="measureDataType"
            :data="data"
            :labels="labels"
            :theme="theme"
            :x-axis-labels="xAxisLabels"
            :y-axis-labels="yAxisLabels"
            :origin="originValue"
            :min="minValue"
            :max="maxValue" />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
  import HeatmapGraph from './HeatmapGraph.vue'
  import ExportButton from '../ExportButton.vue'
  import SnapQLDebugButton from '../SnapQLDebugButton.vue'
  import ShareButton from '../ShareButton.vue'
  import axios from 'axios'

  export default {
    name: 'HeatmapModule',
    components: { HeatmapGraph, ExportButton, SnapQLDebugButton, ShareButton, },
    props: {
      dashboardModule: {
        type: Object,
        required: true,
      },
      dashboardDataContext: {
        type: Object,
        required: true,
      },
      dashboardKey: {
        type: String,
        required: true,
      },
      sharedModuleId: {
        type: String,
        default: undefined,
      },
      isReady: {
        type: Boolean,
        required: true,
      },
    },
    data () {
      return {
        isLoading: false,
        queryError: null,
        cancelTokenSource: null,
        abortController: null,
        measureLabel: '',
        benchmarkValue: null,
        chartData: null,
      }
    },

    watch: {
      dashboardDataContext: {
        handler () {
          this.loadData()
        },
        deep: true,
      },
    },

    computed: {
      theme () {
        if (this.chartData) {
          return this.chartData.theme
        }
        return 'BLUE'
      },
      originValue () {
        if (this.chartData) {
          return this.chartData.scale.origin
        }
        return 0
      },
      minValue () {
        if (this.chartData) {
          return this.chartData.scale.min
        }
        return 0
      },
      maxValue () {
        if (this.chartData) {
          return this.chartData.scale.max
        }
        return 0
      },
      measureDataType () {
        if (this.chartData) {
          return this.chartData.measure_type
        }
        return 0
      },
      xAxisLabels () {
        if (this.chartData) {
          return this.chartData.x_axis.map(
            (xValue) => xValue.label
          )
        }
        return []
      },
      yAxisLabels () {
        if (this.chartData) {
          return this.chartData.y_axis.map(
            (yValue) => yValue.label
          )
        }
        return []
      },
      labels () {
        if (this.chartData) {
          let results = this.chartData.x_axis.map((xValue) =>
            this.chartData.y_axis.map((yValue) => {
              let dataCell = this.chartData.data_cells[
                `${xValue.id},${yValue.id}`
              ]
              return {
                long: dataCell?.label,
                short: dataCell?.short_label,
              }
            })
          )
          return results
        }
        return []
      },
      data () {
        if (this.chartData) {
          let results = []
          this.chartData.x_axis.forEach((xValue, xIdx) => {
            this.chartData.y_axis.forEach((yValue, yIdx) => {
              let dataCell = this.chartData.data_cells[
                `${xValue.id},${yValue.id}`
              ]
              results.push([
                xIdx,
                yIdx,
                dataCell?.value,
              ])
            })
          })
          return results
        }
        return []
      },
    },

    mounted () {
      this.loadData()
    },

    beforeDestroy () {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('Component destroyed')
      }
      if (this.abortController) {
        this.abortController.abort()
      }
    },

    methods: {
      async loadData () {
        if (!this.isReady) {
          return
        }
        const useSnapQL = this.$hasFeatureFlag('USE_SNAPQL')
        if (useSnapQL) {
          return this.loadDataStreaming()
        }
        try {
          if (this.cancelTokenSource) {
            this.cancelTokenSource.cancel('New request initiated')
          }

          this.cancelTokenSource = axios.CancelToken.source()
          this.isLoading = true
          this.queryError = null

          const response = await this.$apis.companyConfigs.getDashboardModuleData(
            this.dashboardKey,
            this.dashboardModule.key,
            this.dashboardDataContext,
            { cancelToken: this.cancelTokenSource.token, }
          )

          if (response?.error) {
            if (response?.code !== 'ERR_CANCELED') {
              this.$buefy.toast.open({
                message: 'Failed to load Heatmap Data',
                type: 'is-warning',
              })
            }
            return
          }

          this.chartData = response
          this.isLoading = false

        } catch (error) {
          console.log(error)
        }
      },
      async loadDataStreaming () {
        if (this.abortController) {
          this.abortController.abort()
        }
        this.abortController = new AbortController()
        this.isLoading = true
        this.queryError = null

        await this.$apis.companyConfigs.streamDashboardModuleData(
          this.dashboardKey,
          this.dashboardModule.key,
          this.dashboardDataContext,
          {
            data: (payload) => {
              this.chartData = payload
            },
            complete: () => {
              this.isLoading = false
            },
            truncated: (payload) => {
              this.$buefy.toast.open({
                message: payload.message,
                type: 'is-warning',
                duration: 8000,
              })
              this.isLoading = false
            },
            error: (err) => {
              this.queryError = err.message || 'Failed to load heatmap data'
              this.isLoading = false
            },
          },
          this.abortController.signal
        )
      },
    },
  }
</script>

<style scoped lang="scss">
.graph-module {
  width: 100%;
  height: 100%;
  display: flex;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  flex-direction: column;
  background-color: white;

  @include mobile {
    border-radius: 0px;
  }
}

.graph-header {
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 16px;
  color: #333;
  padding: 8px;
  display: flex;
  align-items: center;

  .module-name {
    margin-right: 12px;
  }

  .actions {
    margin-left: auto;
  }
}

.graph-body {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.graph-container {
  height: 600px;
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
